import React, { useState, useEffect, useRef, useMemo } from 'react';
import Table, { TableRow, TableCell, TableActionBar } from '@amzn/meridian/table';
import Input from '@amzn/meridian/input';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import Alert from '@amzn/meridian/alert';
import Pagination from '@amzn/meridian/pagination';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Loader from '@amzn/meridian/loader';
import usePagination from '../../hooks/usePagination';
import { ProductItem, ProductTableProps } from '../../types/productTypes';
import useTableSort from '../../hooks/useTableSort';
import useTableFilter from '../../hooks/useTableFilter';

const ProductTable: React.FC<ProductTableProps> = ({
  productGroupData = [],
  productGroupId = '',
  categoryData = '',
  extraDimension = '',
  subcategoryData = '',
  labels = {
    productGroupLabel: 'Product Group',
    categoryLabel: 'Category',
    extraDimensionLabel: 'Extra Dimension',
    subcategoryLabel: 'Subcategory',
    hasExtraDimension: true,
    isExtraDimensionRequired: false,
  },
  isLoading = false,
}) => {
  // set range and default pagination entries
  const [itemsPerPageOptions] = useState([10, 25, 50, 100]);
  const [defaultItemsPerPage] = useState(10);

  // set up filter config to pass in to hook
  const filterConfig = useMemo(
    () => ({
      filterFields: {
        categoryData: 'category',
        extraDimension: 'extraDimension',
        subcategoryData: 'subcategory',
      },
      searchableFields: ['productGroup', 'category', 'extraDimension', 'subcategory'],
      // specify fields that should match by specific ID or by empty values
      idMatchFields: ['subcategoryData'],
    }),
    [],
  );

  const { data, setData, filteredData, setFilteredData, inputValue, setInputValue, applyFilters } = useTableFilter(
    filterConfig,
    productGroupData,
  );

  const { sortColumn, sortDirection, onSort, sortData } = useTableSort<ProductItem>('productGroup');

  // Setup pagination
  const pagination = usePagination({
    itemsPerPage: defaultItemsPerPage,
    totalItems: filteredData.length,
  });

  const prevFiltersRef = useRef({
    categoryData,
    extraDimension,
    subcategoryData,
    inputValue,
  });

  useEffect(() => {
    setData(productGroupData);
  }, [productGroupData, setData]);

  // Filter and sort data
  useEffect(() => {
    let filtered = applyFilters(data, {
      categoryData,
      extraDimension,
      subcategoryData,
      searchValue: inputValue,
    });

    filtered = sortData(filtered);

    // Update filtered data state
    setFilteredData(filtered);

    const prevFilters = prevFiltersRef.current;
    const filtersChanged =
      prevFilters.categoryData !== categoryData ||
      prevFilters.extraDimension !== extraDimension ||
      prevFilters.subcategoryData !== subcategoryData ||
      prevFilters.inputValue !== inputValue;

    if (filtersChanged && pagination.currentPage > 1) {
      const totalPages = Math.max(1, Math.ceil(filtered.length / pagination.itemsPerPage));
      if (pagination.currentPage > totalPages) {
        pagination.resetToFirstPage();
      }
    }

    prevFiltersRef.current = {
      categoryData,
      extraDimension,
      subcategoryData,
      inputValue,
    };
  }, [
    data,
    categoryData,
    extraDimension,
    subcategoryData,
    inputValue,
    sortColumn,
    sortDirection,
    pagination,
    setFilteredData,
    applyFilters,
    sortData,
  ]);

  // Early return after all hooks are called
  if (!productGroupId) {
    return (
      <Column spacing="400" spacingInset="400">
        <Text>Please select a {labels.productGroupLabel} to view the table.</Text>
      </Column>
    );
  }

  // Get paginated data
  const paginatedData = filteredData.slice(pagination.visibleIndices[0], pagination.visibleIndices[1]);

  return (
    <div style={{ width: '100%', maxWidth: '100%', overflowX: 'auto' }}>
      <Table
        headerRows={1}
        showDividers
        spacing="small"
        showStripes={false}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={onSort}
      >
        <TableActionBar widths={[200, 'fill']}>
          <Input
            aria-label="Search"
            type="text"
            size="medium"
            placeholder="Search products..."
            value={inputValue}
            onChange={setInputValue}
          />
          <div />
        </TableActionBar>
        <TableRow backgroundColor="secondary" highlightOnHover>
          <TableCell sortColumn="productGroup">{labels.productGroupLabel}</TableCell>
          <TableCell sortColumn="category">{labels.categoryLabel}</TableCell>
          <TableCell sortColumn="extraDimension">{labels.extraDimensionLabel}</TableCell>
          <TableCell sortColumn="subcategory">{labels.subcategoryLabel}</TableCell>
        </TableRow>
        {isLoading ? (
          <TableRow>
            <TableCell columnSpan={4} alignmentHorizontal="center" alignmentVertical="center">
              <Column spacing="300" alignmentHorizontal="center" spacingInset="600">
                <Loader size="medium" />
                <Text>Loading table data...</Text>
              </Column>
            </TableCell>
          </TableRow>
        ) : filteredData.length > 0 ? (
          paginatedData.map((row) => (
            <TableRow key={row.id} highlightOnHover>
              <TableCell>
                <Column spacingInset="300">{row.productGroup}</Column>
              </TableCell>
              <TableCell>
                <Column spacingInset="300">{row.category}</Column>
              </TableCell>
              <TableCell>
                <Column spacingInset="300">{row.extraDimension}</Column>
              </TableCell>
              <TableCell>
                <Column spacingInset="300">{row.subcategory}</Column>
              </TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell columnSpan={4} alignmentHorizontal="center" alignmentVertical="center">
              <Alert size="large"> No products match </Alert>
            </TableCell>
          </TableRow>
        )}
      </Table>

      {filteredData.length > 0 && (
        <Row spacing="400" spacingInset="400" alignmentHorizontal="start" alignmentVertical="center">
          <Row spacing="200" alignmentVertical="center">
            <Text>Rows per page:</Text>
            <Select
              value={pagination.itemsPerPage.toString()}
              onChange={(value) => pagination.handleItemsPerPageChange(Number(value))}
              width={80}
            >
              {itemsPerPageOptions.map((option) => (
                <SelectOption key={option} label={option.toString()} value={option.toString()} />
              ))}
            </Select>
            <Text>
              Showing {pagination.visibleIndices[0] + 1} to {pagination.visibleIndices[1]} of {filteredData.length}{' '}
              items
            </Text>
          </Row>
          <Pagination
            currentPage={pagination.currentPage}
            numberOfPages={pagination.totalPages}
            onChange={pagination.handlePageChange}
          />
        </Row>
      )}
    </div>
  );
};

export default ProductTable;
